import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Clock, Tag, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      setService(r.data);
      setPkg(r.data.packages?.[0]?.name || null);
    }).catch(() => setNotFound(true));
  }, [slug]);

  const order = () => {
    const selectedPrice = service.packages?.find((p) => p.name === pkg)?.price ?? service.price;
    const payload = { service_id: service.id, service_name: service.name, package_name: pkg, price: selectedPrice };
    sessionStorage.setItem("adnexa_checkout", JSON.stringify(payload));
    if (!user) { toast.info(t("checkout.loginRequired")); nav("/login?redirect=/checkout"); return; }
    nav("/checkout");
  };

  if (notFound) return <div className="section-lux container-lux text-center"><h1 className="text-3xl font-bold text-white">Service not found</h1><Link to="/services" className="gold-btn mt-6 inline-flex">{t("nav.services")}</Link></div>;
  if (!service) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-amber-400" size={40} /></div>;

  return (
    <div className="section-lux grid-glow">
      <Seo title={service.seo_title || service.name} description={service.seo_description || service.short_description}
        image={service.thumbnail || undefined}
        schema={{ "@context": "https://schema.org", "@type": "Service", name: service.name,
          description: service.short_description, category: service.category,
          offers: { "@type": "Offer", price: service.price, priceCurrency: "USDT" },
          provider: { "@type": "Organization", name: "ADNEXA DIGITAL" } }} />
      <div className="container-lux grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="label-lux mb-3">{service.category}</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white">{service.name}</h1>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-400">
            {service.delivery_time && <span className="flex items-center gap-1.5"><Clock size={15} className="text-amber-400" /> {service.delivery_time}</span>}
            <span className="flex items-center gap-1.5"><Tag size={15} className="text-amber-400" /> {t("common.from")} {service.price} USDT</span>
          </div>
          <p className="mt-6 text-slate-300 leading-relaxed whitespace-pre-line">{service.full_description || service.short_description}</p>

          {service.features?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {service.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check size={16} className="text-amber-400 mt-0.5 shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          {service.faq?.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-white mb-4">FAQ</h3>
              <Accordion type="single" collapsible>
                {service.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="card-lux mb-3 px-5 border-none">
                    <AccordionTrigger className="text-left text-white hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-400">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>

        {/* order box */}
        <div className="lg:col-span-1">
          <div className="card-lux p-6 sticky top-24" data-testid="service-order-box">
            <h3 className="font-semibold text-white mb-4">{service.order_button_text || t("common.orderNow")}</h3>
            {service.packages?.length > 0 ? (
              <div className="space-y-3">
                {service.packages.map((p) => (
                  <button key={p.name} onClick={() => setPkg(p.name)} data-testid={`package-option-${p.name}`}
                    className={`w-full text-left rounded-lg border p-4 transition-all ${pkg === p.name ? "border-amber-500 bg-amber-500/5" : "border-white/10 hover:border-amber-500/40"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{p.name}</span>
                      <span className="font-display font-bold text-amber-400">{p.price} USDT</span>
                    </div>
                    {p.description && <p className="text-xs text-slate-400 mt-1">{p.description}</p>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="font-display font-bold text-2xl text-white">{service.price} <span className="text-amber-400 text-base">USDT</span></div>
              </div>
            )}
            <button onClick={order} className="gold-btn w-full mt-5" data-testid="service-order-now-btn">
              {t("common.orderNow")} <ArrowRight size={16} />
            </button>
            <p className="text-xs text-slate-500 mt-3 text-center">Secure USDT checkout · Verified payments</p>
          </div>
        </div>
      </div>
    </div>
  );
}
